// Theme provider for the Cutting Simulator module.
//
// The components imported from the `maquina-de-corte` zip consume a `t` object
// of Tailwind class fragments (t.bg, t.card, t.text, …) plus `mode` (`'dark'`
// or `'light'`) and a `toggle()` function. This file provides those tokens.
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const LIGHT = {
  bg: "bg-slate-50",
  card: "bg-white border border-slate-200 shadow-sm",
  cardFlat: "bg-white border border-slate-200",
  inner: "bg-slate-100 border border-slate-200",
  inputBg: "bg-white",
  border: "border-slate-200",
  divider: "border-slate-200",
  hover: "hover:bg-slate-100",
  hoverCard: "hover:border-slate-300",
  text: "text-slate-900",
  textBody: "text-slate-700",
  textSec: "text-slate-600",
  textLabel: "text-slate-700",
  textMut: "text-slate-500",
  success: "text-emerald-600",
  error: "text-red-600",
  warning: "text-amber-600",
  info: "text-sky-600",
  icon: "text-slate-500",
  texture: "",
};

const DARK = {
  bg: "bg-slate-950",
  card: "bg-slate-900 border border-slate-800 shadow-md",
  cardFlat: "bg-slate-900 border border-slate-800",
  inner: "bg-slate-900/60 border border-slate-800",
  inputBg: "bg-slate-900",
  border: "border-slate-800",
  divider: "border-slate-800",
  hover: "hover:bg-slate-800",
  hoverCard: "hover:border-slate-700",
  text: "text-slate-100",
  textBody: "text-slate-200",
  textSec: "text-slate-300",
  textLabel: "text-slate-300",
  textMut: "text-slate-400",
  success: "text-emerald-400",
  error: "text-red-400",
  warning: "text-amber-400",
  info: "text-sky-400",
  icon: "text-slate-400",
  texture: "",
};

const CuttingThemeContext = createContext({ mode: "light", toggle: () => {}, t: LIGHT });

export function CuttingThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") return "light";
    try {
      const saved = window.localStorage.getItem("cutting-theme");
      if (saved === "dark" || saved === "light") return saved;
    } catch (e) { /* noop */ }
    return "light";
  });

  const toggle = useCallback(() => {
    setMode((m) => (m === "dark" ? "light" : "dark"));
  }, []);

  useEffect(() => {
    try { window.localStorage.setItem("cutting-theme", mode); } catch (e) { /* noop */ }
  }, [mode]);

  const value = useMemo(() => ({
    mode,
    toggle,
    t: mode === "dark" ? DARK : LIGHT,
  }), [mode, toggle]);

  return React.createElement(CuttingThemeContext.Provider, { value }, children);
}

export function useCuttingTheme() {
  return useContext(CuttingThemeContext);
}
